import { Badge } from "@/components/ui";
import { BodyText } from "@/components/ui/Text";
import { cn } from "@/lib/utils";
import type {
  AllergyIntoleranceType,
  ReactionEventSeverity,
} from "@curai/js-fhir-tools";
import type { ExtractedSymptom } from "./useAmazonVisitIntake";

const SEVERITY_CLASSES: Record<ReactionEventSeverity, string> = {
  mild: "bg-info text-primary",
  moderate: "bg-warning text-primary",
  severe: "bg-error text-primary",
};

const SEVERITY_LABEL: Record<ReactionEventSeverity, string> = {
  mild: "Mild",
  moderate: "Moderate",
  severe: "Severe",
};

const TYPE_LABEL: Record<AllergyIntoleranceType, string> = {
  allergy: "Allergy",
  intolerance: "Intolerance",
};

export const CiqAllergyMetadata = ({
  type,
  severity,
  symptoms,
}: {
  type?: AllergyIntoleranceType | null;
  severity?: ReactionEventSeverity | null;
  symptoms: ExtractedSymptom[];
}) => {
  const hasMeta = Boolean(type || severity || symptoms.length);
  if (!hasMeta) return null;

  return (
    <div className="mt-1 flex flex-col gap-1">
      {(severity || type) && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {severity && (
            <Badge
              variant="default"
              className={cn(
                "px-1.5 py-0 uppercase",
                SEVERITY_CLASSES[severity],
              )}
            >
              <BodyText size="xs" bold>
                {SEVERITY_LABEL[severity]}
              </BodyText>
            </Badge>
          )}
          {type && (
            <Badge variant="light" className="px-1.5 py-0">
              <BodyText size="xs">{TYPE_LABEL[type]}</BodyText>
            </Badge>
          )}
        </div>
      )}
      {symptoms.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <BodyText size="xs" className="text-secondary">
            Symptoms:
          </BodyText>
          {symptoms.map((s) => (
            <Badge
              key={s.symptom}
              variant="light"
              className="px-1.5 py-0 border border-tertiary"
            >
              <BodyText size="xs">{s.symptom}</BodyText>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
